// Shared primitives: brand mark, icons, sparkline, meter
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";

// Venzyme brand mark: four parallelogram tiles — a modern re-reading of the
// parent Venzyme Venture Catalyst mark (catalytic-site metaphor). Three lighter
// tiles + one darker "active site" tile, arranged on a slight axis.
export function BrandMark({ size = 28 }) {
  // Each tile is a slanted parallelogram. Offset creates a forward-slash stack.
  const tile = (x, y, fill) => (
    <path
      d={`M ${x} ${y} l 7 0 l -3 8 l -7 0 z`}
      fill={fill}
    />
  );
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden>
      {/* bottom-left tile — softest */}
      {tile(3,  21, "color-mix(in oklab, var(--accent) 35%, transparent)")}
      {/* mid-lower tile */}
      {tile(10, 17, "color-mix(in oklab, var(--accent) 55%, transparent)")}
      {/* mid-upper tile */}
      {tile(17, 13, "color-mix(in oklab, var(--accent) 78%, transparent)")}
      {/* top-right "active-site" tile — fully saturated */}
      {tile(24, 9,  "var(--accent-hi)")}
    </svg>
  );
}

// Tiny icon set — stroke-based, consistent weight
export const Icon = {
  home: <path d="M3 11l9-8 9 8M5 9v12h14V9" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>,
  upload: <path d="M12 4v12M6 10l6-6 6 6M4 20h16" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>,
  pipeline: <path d="M4 6h8l4 4v8H4zM12 6v4h4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>,
  chart: <path d="M4 20V4M4 20h16M8 16v-6M12 16V8M16 16v-4M20 16V6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>,
  folder: <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>,
  search: <g><circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M20 20l-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></g>,
  people: <g><circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none"/><circle cx="17" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M3 19c0-3 3-5 6-5s6 2 6 5M21 19c0-2-2-3.5-4-3.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/></g>,
  gear: <g><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></g>,
  shield: <path d="M12 3l8 3v6c0 5-4 8-8 9-4-1-8-4-8-9V6z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>,
  doc: <path d="M6 3h8l4 4v14H6zM14 3v4h4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>,
  beaker: <path d="M9 3h6M10 3v6L5 20h14L14 9V3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>,
  plus: <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>,
  check: <path d="M5 12l5 5L20 6" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>,
  x: <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>,
  dots: <g><circle cx="5" cy="12" r="1.4" fill="currentColor"/><circle cx="12" cy="12" r="1.4" fill="currentColor"/><circle cx="19" cy="12" r="1.4" fill="currentColor"/></g>,
};

export function Ico({ n, size = 16 }) {
  return <svg viewBox="0 0 24 24" width={size} height={size}>{Icon[n]}</svg>;
}

// Sparkline — simple line with light fill
export function Spark({ data, stroke = "var(--accent-hi)", fill = "var(--accent-soft)" }) {
  const w = 220, h = 44, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (w - pad * 2) + pad;
    const y = h - pad - ((v - min) / rng) * (h - pad * 2);
    return [x, y];
  });
  const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const df = d + ` L${w - pad} ${h} L${pad} ${h} Z`;
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={df} fill={fill} stroke="none"/>
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5"/>
    </svg>
  );
}

// IAS meter: semicircular dial, numeric readout. Instrument-feeling.
export function IASMeter({ value = 68, size = 180, label = "IAS" }) {
  const r = size / 2 - 12;
  const cx = size / 2, cy = size / 2;
  const start = Math.PI, end = 2 * Math.PI;  // semicircle top
  const arc = (from, to) => {
    const x1 = cx + r * Math.cos(from), y1 = cy + r * Math.sin(from);
    const x2 = cx + r * Math.cos(to),   y2 = cy + r * Math.sin(to);
    const large = to - from > Math.PI ? 1 : 0;
    return `M${x1} ${y1} A${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };
  const valAngle = start + (value / 100) * (end - start);
  return (
    <svg viewBox={`0 0 ${size} ${size/1.6}`} width={size} height={size/1.6}>
      <path d={arc(start, end)} stroke="var(--line-strong)" strokeWidth="6" fill="none" strokeLinecap="round"/>
      <path d={arc(start, valAngle)} stroke="var(--accent-hi)" strokeWidth="6" fill="none" strokeLinecap="round"/>
      {/* ticks */}
      {[0, .25, .5, .75, 1].map(t => {
        const a = start + t * (end - start);
        const x1 = cx + (r - 10) * Math.cos(a), y1 = cy + (r - 10) * Math.sin(a);
        const x2 = cx + (r + 2) * Math.cos(a), y2 = cy + (r + 2) * Math.sin(a);
        return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--text-dim)" strokeWidth="1"/>;
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" fontFamily="var(--ff-h)" fontSize="36" fill="var(--text)" style={{fontVariationSettings:'"opsz" 144'}}>
        {Math.round(value)}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontFamily="var(--ff-mono)" fontSize="10" letterSpacing="2" fill="var(--text-dim)">
        {label}
      </text>
    </svg>
  );
}

// Radar chart for IAS breakdown
export function Radar({ data, size = 260 }) {
  const cx = size/2, cy = size/2, r = size/2 - 30;
  const n = data.length;
  const angle = i => -Math.PI/2 + (i / n) * Math.PI * 2;
  const point = (i, v) => {
    const a = angle(i);
    const rr = (v/100) * r;
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  };
  const poly = data.map((d, i) => point(i, d.score).map(n=>n.toFixed(1)).join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" height={size}>
      {[.25,.5,.75,1].map((t,i) => (
        <polygon key={i}
          points={data.map((_, j) => {
            const a = angle(j);
            return `${(cx + r*t*Math.cos(a)).toFixed(1)},${(cy + r*t*Math.sin(a)).toFixed(1)}`;
          }).join(" ")}
          fill="none" stroke="var(--line)" strokeWidth="1"/>
      ))}
      {data.map((_, i) => {
        const [x, y] = point(i, 100);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line)" strokeWidth="1"/>;
      })}
      <polygon points={poly} fill="var(--accent-soft)" stroke="var(--accent-hi)" strokeWidth="1.5"/>
      {data.map((d, i) => {
        const [lx, ly] = point(i, 122);
        return (
          <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                fontFamily="var(--ff-mono)" fontSize="10" letterSpacing="1.2"
                fill="var(--text-mute)" style={{textTransform:"uppercase"}}>
            {d.key}
          </text>
        );
      })}
    </svg>
  );
}

export function StatusPill({ status }) {
  const map = {
    diligence: { label: "Diligence", cls: "pill--accent" },
    shortlist: { label: "Shortlist", cls: "pill--good" },
    review: { label: "In review", cls: "" },
    pass: { label: "Passed", cls: "pill--bad" },
    scored: { label: "Scored", cls: "pill--good" }
  };
  const m = map[status] || { label: status, cls: "" };
  return <span className={"pill " + m.cls}><i className="dot"/>{m.label}</span>;
}
